/**
 * Mirrors the backend's auth contract exactly — see
 * `backend/apps/accounts/serializers.py`. The API returns camelCase for these
 * endpoints specifically so no reshaping happens here. Changing a field name
 * on either side breaks sign-in at runtime with no compile error, so both
 * sides assert the shape in tests.
 */

import type { JobRole } from './trackheros'

export type SignInCredential = {
    email: string
    password: string
}

export type SignInResponse = {
    token: string
    refreshToken: string
    user: User
}

export type SignUpResponse = SignInResponse

export type SignUpCredential = {
    email: string
    password: string
}

/**
 * The ID token Google Identity Services hands the browser. It is not a
 * credential we trust — the backend verifies its signature and audience before
 * it means anything (backend `apps/accounts/oauth.py`).
 */
export type OauthCredential = {
    credential: string
}

/**
 * What the browser posts to `/auth/firebase`.
 *
 * `idToken` is a Firebase ID token, whichever provider produced it. The extras
 * are no longer sent by the web sign-up form, which asks only for an email and
 * password — every new account answers the job-role question on `/welcome`.
 * They stay in the contract because the backend still honours them for mobile
 * builds that do send them.
 */
export type FirebaseExchangeRequest = {
    idToken: string
    userName?: string
    jobRole?: JobRole | ''
    jobRoleOther?: string
}

/** What the backend sends when an address has not been confirmed yet. */
export const EMAIL_NOT_VERIFIED = 'email_not_verified'

/**
 * What the backend sends instead of a session while the account waits for
 * approval — the private beta (backend `apps/accounts/access.py`).
 *
 * The account exists by then; only the session is withheld. Every endpoint
 * that hands out tokens can answer this, whichever provider was used.
 */
export const ACCESS_PENDING = 'access_pending'

export type AccessPendingResponse = {
    detail: string
    code: typeof ACCESS_PENDING
    /** Which address is waiting — after a Google or Microsoft popup the
     *  browser has no other way to know which of several accounts it was. */
    email: string
    /** False when somebody already waiting signs in again. */
    isNewRequest: boolean
}

/** Router state handed to `/waitlist`. Absent on a direct visit. */
export type WaitlistState = {
    email?: string
    isNewRequest?: boolean
}

/**
 * What the backend sends instead of a session when the account has two-factor
 * on (backend `apps/accounts/twofactor.py`). The first step of the sign-in —
 * password, Google or Microsoft — succeeded; no session exists until the
 * challenge and a code are posted to `/auth/two-factor/verify`.
 */
export const TWO_FACTOR_REQUIRED = 'two_factor_required'
/** A wrong code: try again with the same challenge. */
export const TWO_FACTOR_INVALID = 'two_factor_invalid'
/** The challenge is spent, stale or out of attempts: sign in again. */
export const TWO_FACTOR_EXPIRED = 'two_factor_expired'
/** Too many wrong codes on this account; comes with `retryAfter` seconds. */
export const TWO_FACTOR_LOCKED = 'two_factor_locked'

export type TwoFactorRequiredResponse = {
    detail: string
    code: typeof TWO_FACTOR_REQUIRED
    /** Opaque and single-use. Never put it in a URL. */
    challenge: string
    email: string
    /** Seconds the challenge stays usable. */
    expiresIn: number
}

/** Router state handed to `/two-factor`. Absent on a direct visit or a reload. */
export type TwoFactorState = {
    challenge?: string
    email?: string
    expiresIn?: number
}

export type TwoFactorVerifyRequest = {
    challenge: string
    code: string
}

export type TwoFactorStatus = {
    enabled: boolean
    enabledAt: string | null
    recoveryCodesRemaining: number
}

export type TwoFactorSetupResponse = {
    /** Base32, for typing in by hand when the QR code cannot be scanned. */
    secret: string
    otpauthUri: string
}

export type RecoveryCodesResponse = {
    /** The only time these exist in plaintext. Show once, never store. */
    recoveryCodes: string[]
}

export type FirebaseMigrateRequest = {
    email: string
    password: string
}

/**
 * Same three keys as a password sign-in, so the session-establishing path is
 * shared, plus `isNewUser` — a one-click sign-up never sees the sign-up form,
 * so this is what tells us to ask the job-role question afterwards.
 */
export type OauthSignInResponse = SignInResponse & {
    isNewUser: boolean
}

export type RefreshResponse = {
    token: string
    refreshToken: string
}

export type ForgotPassword = {
    email: string
}

export type ResetPassword = {
    password: string
}

export type AuthRequestStatus = 'success' | 'failed' | ''

export type AuthResult = Promise<{
    status: AuthRequestStatus
    message: string
}>

/**
 * `reason` tells the two-factor screen what to offer next: another try, a
 * fresh sign-in, or a wait of `retryAfter` seconds.
 */
export type TwoFactorResult = Promise<{
    status: AuthRequestStatus
    message: string
    reason?: 'invalid' | 'expired' | 'locked'
    retryAfter?: number
}>

/**
 * The account a user belongs to.
 *
 * Sent on every auth response because almost every screen needs it: `kind`
 * decides whether the company features appear at all, and `status` is what the
 * suspended-account banner reads. Null only between sign-up and the moment the
 * backend provisions the organisation, which in practice is the same request.
 */
export type Organization = {
    id: string
    name: string
    kind: 'personal' | 'enterprise'
    status: 'active' | 'suspended'
}

export type OrgRole = 'owner' | 'admin' | 'member'

export type User = {
    userId?: string | null
    avatar?: string | null
    userName?: string | null
    email?: string | null
    authority?: string[]
    organization?: Organization | null
    role?: OrgRole | null
}

export type Token = {
    accessToken: string
    refreshToken?: string
}

export type OauthSignInCallbackPayload = {
    onSignIn: (tokens: Token, user?: User) => void
    redirect: () => void
}
